import type { SheetColumn, SheetRow, SheetSelectOption } from '../schemas/sheets';
import { formatDate, parseDate, parseDateEntry } from './date';
import { phoneFieldForKey } from '../constants/phone-columns';
import { parseInternationalPhoneList, type InternationalPhoneCountry } from './international-phone';
import { normalizePhone } from './phone';

/**
 * Значение ячейки листа: показать, отдать в редактор, разобрать обратно.
 *
 * Живёт в общем пакете, а не в компоненте: по этим же правилам сервер
 * ищет по листу и собирает выгрузку. Пока формат даты был написан
 * отдельно на фронте и на бэке, поиск по «12.08» не находил строку,
 * которая на экране так и выглядела.
 */

/** Значение ячейки: то, что лежит в `data`, или посчитанное сервером. */
export function sheetRawValue(row: SheetRow, column: SheetColumn): unknown {
  if (column.type === 'computed') return row.computed?.[column.key] ?? null;
  const raw = row.data[column.key];
  // Ручная дата окончания старше расчёта. Сам расчёт остаётся вне data:
  // редактор и Undo должны различать введённую дату и пустой источник.
  return raw === null || raw === undefined || raw === ''
    ? (row.computed?.[column.key] ?? raw ?? null) : raw;
}

/** Подпись прошедшей вахты; числовой остаток сохраняется для расчётов и сортировки. */
export function sheetCompletionLabel(row: SheetRow, column: SheetColumn): string | null {
  if (column.type !== 'computed' || column.key !== 'daysLeft'
    || column.rule?.kind !== 'daysUntil' || column.rule.from !== 'finishDate') return null;
  const value = sheetRawValue(row, column);
  return typeof value === 'number' && Number.isFinite(value) && value < 0 ? 'Завершилась' : null;
}

/** Страна маски: исторические колонки «БАЗЫ» и типизированные поля листов. */
export function sheetPhoneCountry(column: SheetColumn): InternationalPhoneCountry {
  const key = column.sourceField ?? column.key;
  if (key === 'phoneBy') return 'BY';
  if (key === 'phoneRu') return 'RU';
  return phoneFieldForKey(key)?.country ?? 'OTHER';
}

function sheetPhones(column: SheetColumn, value: unknown) {
  const country = sheetPhoneCountry(column);
  return parseInternationalPhoneList(String(value), country).map((item) => ({
    ...item,
    // В дополнительных колонках старой книги есть национальные РБ/РФ.
    // Пометки с цифрами не превращаем в номер и не теряем при открытии.
    normalized:
      item.normalized ??
      (country === 'OTHER' && /^\+?[\d\s().-]+$/.test(item.raw) ? normalizePhone(item.raw) : null),
  }));
}

function displayPhones(column: SheetColumn, value: unknown): string {
  return sheetPhones(column, value)
    .map(({ raw, normalized }) => normalized?.formatted ?? raw)
    .join('\n');
}

export function optionFor(column: SheetColumn, value: unknown): SheetSelectOption | null {
  if (value === null || value === undefined) return null;
  const s = String(value);
  return column.options.find((o) => o.value === s) ?? null;
}

/**
 * Число с разделителями разрядов. Пробел неразрывный: иначе «3 993 ₽»
 * переносится по строке ровно между разрядом и знаком рубля.
 */
// Форматтер одинаков для всех ячеек: его создание значительно дороже format().
const sheetNumberFormatter = new Intl.NumberFormat('ru-RU');

export function formatSheetNumber(value: number, money: boolean): string {
  /*
    Неразрывные пробелы записаны кодом, а не символом: линтер запрещает
    их в исходнике (no-irregular-whitespace), потому что от обычного
    пробела они в редакторе не отличаются.

    Разделитель разрядов Intl тоже отдаёт неразрывным, но узким —
    его меняем на обычный неразрывный, иначе «3 993 ₽» рвётся по строке
    ровно между разрядом и знаком рубля.
  */
  const nbsp = '\u00a0';
  const text = sheetNumberFormatter.format(value).replace(/\s/g, nbsp);
  return money ? `${text}${nbsp}\u20bd` : text;
}

/**
 * Показ значения по формату колонки.
 *
 * Формат отделён от типа, потому что тип менять нельзя — в строках уже
 * лежат значения, — а «показать сумму без копеек» или «дату с днём
 * недели» просят часто. `plain` означает «как велит тип», и это то,
 * что было до появления формата: у всех колонок книги стоит именно он.
 */
function applyFormat(column: SheetColumn, value: unknown): string | null {
  switch (column.format) {
    case 'number':
    case 'grouped':
    case 'money':
    case 'percent': {
      const n = Number(value);
      if (!Number.isFinite(n)) return null;
      if (column.format === 'percent') return `${formatSheetNumber(n, false)}%`;
      if (column.format === 'number') return String(n);
      return formatSheetNumber(n, column.format === 'money');
    }
    case 'date':
      return formatDate(String(value));
    case 'dateWeekday': {
      const text = formatDate(String(value));
      const date = new Date(`${String(value)}T00:00:00`);
      if (Number.isNaN(date.getTime())) return text;
      const weekday = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'][date.getDay()];
      return `${text}, ${weekday}`;
    }
    case 'phone':
      return displayPhones(column, value);
    case 'text':
      return String(value);
    case 'plain':
    default:
      return null;
  }
}

/** Текст ячейки для показа. */
export function sheetDisplayValue(row: SheetRow, column: SheetColumn): string {
  /*
    Ссылка на строку другого листа и сотрудник: в `data` идентификатор,
    подпись приходит отдельно.

    Если подписи нет — показываем то, что лежит в ячейке. Иначе значение,
    которое не сошлось с учёткой, исчезает с экрана: на «Занятости» в графе
    дежурного встречается «Света + Ева» — двое разом, одной учёткой это
    не выражается. Пустая ячейка вместо этого читается как «никто
    не дежурит», а дежурили двое.
  */
  if (column.type === 'ref' || column.type === 'user') {
    const label = row.labels[column.key];
    if (label) return label;
    const raw = row.data[column.key];
    return raw === null || raw === undefined ? '' : String(raw);
  }

  const value = sheetRawValue(row, column);
  if (value === null || value === undefined || value === '') return '';

  const completionLabel = sheetCompletionLabel(row, column);
  if (completionLabel) return completionLabel;

  const formatted = applyFormat(column, value);
  if (formatted !== null) return formatted;

  /*
    Вычисляемая колонка своего типа не имеет: одно и то же правило отдаёт
    и дату («дата выезда плюс смены»), и число («дней осталось»). Поэтому
    вид выбирается по самому значению — иначе дата окончания вахты
    показывалась бы как «2026-08-03» посреди колонок с «03.08.2026».
  */
  if (column.type === 'computed') {
    if (typeof value === 'number') return formatSheetNumber(value, false);
    const text = String(value);
    return /^\d{4}-\d{2}-\d{2}$/.test(text) ? formatDate(text) : text;
  }

  switch (column.type) {
    case 'boolean':
      return value ? 'да' : '';
    case 'date':
      return formatDate(String(value));
    case 'phone':
      return displayPhones(column, value);
    case 'number':
    case 'money': {
      const n = Number(value);
      return Number.isFinite(n) ? formatSheetNumber(n, column.type === 'money') : String(value);
    }
    case 'select':
      return optionFor(column, value)?.label ?? String(value);
    case 'multiselect':
      return (Array.isArray(value) ? value : [value])
        .map((v) => optionFor(column, v)?.label ?? String(v))
        .join(', ');
    default:
      return String(value);
  }
}

/** Значение в поле редактора: правится то, что человек ввёл, а не вид. */
export function sheetEditValue(row: SheetRow, column: SheetColumn): string {
  const value = sheetRawValue(row, column);
  if (value === null || value === undefined) return '';
  if (column.type === 'date') return formatDate(String(value));
  // Дополнительный номер без кода страны открывается полной маской.
  // Сохранение решает редактор: само открытие это значение не записывает.
  if (column.type === 'phone') return displayPhones(column, value);
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * Разбор введённого текста в значение для хранения.
 *
 * Исходов три, а не два. Пустая строка — `null`: иначе «пусто» в базе
 * окажется двух видов, и фильтр «не заполнено» половину строк не найдёт.
 * Неразобранный текст — `undefined`, и он не пишется вовсе.
 *
 * Разделение появилось из потери данных, а не из аккуратности. Числовая
 * ячейка вычищала всё, кроме цифр, и звала `Number('')` — а это ноль,
 * и он конечен. Набранное мимо поля «абв» поверх суммы 3993 давало
 * правдоподобный ноль, которого не находил даже фильтр «не заполнено»:
 * ячейка выглядела заполненной, потому что она и была заполнена.
 *
 * `typed` ставит только редактор ячейки: сокращённая дата — помощь тому,
 * кто набирает вручную, а не правило разбора чужих данных.
 */
export function parseSheetInput(
  column: SheetColumn,
  text: string,
  opts?: { typed?: boolean },
): unknown {
  const s = text.trim();
  if (!s) return null;

  switch (column.type) {
    case 'number':
    case 'money': {
      // «3 993 ₽», «4.500₽», «от 3993» — из книги значения приходят так,
      // и человек продолжит их так набирать
      const digits = s
        .replace(/[^\d,.-]/g, '')
        .replace(/\.(?=\d{3}\b)/g, '')
        .replace(',', '.');
      if (!/\d/.test(digits)) return undefined;
      const n = Number(digits);
      return Number.isFinite(n) ? n : undefined;
    }
    case 'date':
      // Сокращения («5», «5.11») разворачиваются только под рукой человека:
      // вставке из буфера они не достаются, см. `parseDateEntry`
      return (opts?.typed ? parseDateEntry(s) : parseDate(s)) ?? undefined;
    case 'boolean':
      return !/^(нет|no|false|0|-)$/i.test(s);
    case 'select':
    case 'user':
      return selectInputValue(column, s);
    case 'multiselect': {
      const parts = s
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      const values = parts.map((part) => selectInputValue(column, part));
      return values.some((value) => value === undefined) ? undefined : [...new Set(values)];
    }
    default:
      return s;
  }
}

/** Буфер содержит подписи. Не угадываем код, если одна подпись у двух вариантов. */
function selectInputValue(column: SheetColumn, input: string): string | undefined {
  if (column.options.some((option) => option.value === input)) return input;
  const labels = column.options.filter(
    (option) => option.label?.toLocaleLowerCase('ru-RU') === input.toLocaleLowerCase('ru-RU'),
  );
  if (labels.length > 1) return undefined;
  return labels[0]?.value ?? input;
}

/**
 * Текст ячейки для буфера обмена.
 *
 * Переводы строк сохраняются: условия размещения в «Рекл.РБ» лежат
 * в ячейке в несколько строк, и схлопывание их в пробел означало, что
 * скопированное из системы возвращается обратно другим. Разделить
 * ячейки от строк — забота `toTsv`, он их и экранирует.
 */
export function sheetClipboardValue(row: SheetRow, column: SheetColumn): string {
  if (column.type === 'phone') {
    const value = sheetRawValue(row, column);
    return value === null || value === undefined
      ? ''
      : sheetPhones(column, value)
          .map(({ raw, normalized }) => (normalized ? `+${normalized.e164}` : raw))
          .join(', ');
  }
  return sheetDisplayValue(row, column);
}

/**
 * Равенство значений двух ячеек.
 *
 * Понадобилось складыванию повторов: там сравнение шло через
 * `JSON.stringify` по всем строкам листа на каждую перерисовку —
 * полторы тысячи разборов и сборок строк ради проверки «то же самое?».
 */
export function sameCellValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((v, i) => v === b[i]);
  return false;
}

/** Первая веб-ссылка в ячейке: её просмотр всегда занимает одну строку. */
export function sheetLinkHref(text: string): string | null {
  return text.trim().match(/https?:\/\/\S+/)?.[0] ?? null;
}
